import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import VoicemailOutlinedIcon from "@mui/icons-material/VoicemailOutlined"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Link from "@mui/material/Link"
import MenuItem from "@mui/material/MenuItem"
import TableCell from "@mui/material/TableCell"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import PropTypes from "prop-types"
import { useEffect, useMemo, useState } from "react"

import Row from "../../../common/components/Row"
import { trimLongString } from "../../../common/utils"
import { adminApi } from "../axios"
import AdminActivityThreadDrawer from "../components/AdminActivityThreadDrawer"
import AdminExportCsvButton from "../components/AdminExportCsvButton"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import AdminVoicemailTranscriptCell from "../components/AdminVoicemailTranscriptCell"
import {
  normalizeStaffListResponse,
  useAdminPaginatedQuery,
} from "../hooks/useAdminPaginatedQuery"
import {
  ACTIVITY_TYPE_FILTERS,
  activityRowKind,
  actorLabel,
  counterpartyLabel,
  formatDurationSeconds,
} from "./activityFeed"

const SUMMARY_PREVIEW_LIMIT = 48

const ROW_ICONS = {
  call: <PhoneOutlinedIcon fontSize="small" />,
  voicemail: <VoicemailOutlinedIcon fontSize="small" />,
  text: <SmsOutlinedIcon fontSize="small" />,
}

function formatWhen(iso, pattern = "MMM d, h:mm a") {
  return iso ? format(new Date(iso), pattern) : "—"
}

function KindCell({ row }) {
  const kind = activityRowKind(row)
  return (
    <TableCell sx={adminTableBodyCellSx}>
      <Chip
        size="small"
        variant="outlined"
        color={kind.color}
        icon={ROW_ICONS[row.type]}
        label={kind.label}
      />
    </TableCell>
  )
}

KindCell.propTypes = { row: PropTypes.object.isRequired }

function SummaryCell({ row, onOpenThread }) {
  if (row.type === "voicemail") {
    return (
      <AdminVoicemailTranscriptCell
        transcript={row.body}
        fromPhone={row.counterparty?.phone}
        created={formatWhen(row.timestamp, "MMM d, yyyy h:mm a")}
        conciergeName={row.actor?.name}
        ownerName={row.account_owner?.name}
      />
    )
  }

  const duration =
    row.type === "call" ? formatDurationSeconds(row.duration_seconds) : ""
  const text =
    row.type === "call"
      ? [row.summary, duration].filter(Boolean).join(" · ")
      : row.summary
  return (
    <TableCell
      sx={{ ...adminTableBodyCellSx, maxWidth: { xs: 200, sm: 280, md: 360 } }}
    >
      <Row gap={1} sx={{ alignItems: "center", minWidth: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={row.type === "text" ? row.body || "" : text}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {text ? trimLongString(text, SUMMARY_PREVIEW_LIMIT) : "—"}
        </Typography>
        {row.type === "text" && row.thread ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onOpenThread(row.thread)}
            sx={{ flexShrink: 0 }}
          >
            Thread
          </Button>
        ) : null}
      </Row>
    </TableCell>
  )
}

SummaryCell.propTypes = {
  row: PropTypes.object.isRequired,
  onOpenThread: PropTypes.func.isRequired,
}

function RecordingCell({ row }) {
  return (
    <TableCell sx={adminTableBodyCellSx}>
      {row.recording_url ? (
        <Link href={row.recording_url} target="_blank" rel="noopener">
          Play
        </Link>
      ) : row.type === "text" ? (
        "—"
      ) : (
        row.recording_status || "—"
      )}
    </TableCell>
  )
}

RecordingCell.propTypes = { row: PropTypes.object.isRequired }

/**
 * Calls (incl. AMD outcome), inbound voicemails and texts for one concierge,
 * newest first. Replaces the separate call-log and voicemail tabs.
 */
export default function ConciergeActivityTab({ conciergeId, active }) {
  const [type, setType] = useState("all")
  const [accountOwnerId, setAccountOwnerId] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [owners, setOwners] = useState([])
  const [openThread, setOpenThread] = useState(null)

  useEffect(() => {
    if (!active) return undefined
    let cancelled = false
    adminApi
      .fetchAssignments({
        concierge_user_id: conciergeId,
        status: "all",
        page: 1,
        page_size: 100,
      })
      .then(({ data }) => {
        if (cancelled) return
        const byId = new Map()
        normalizeStaffListResponse(data).results.forEach((row) => {
          if (row.owner_user_id && !byId.has(row.owner_user_id)) {
            byId.set(row.owner_user_id, row.owner_name || row.owner_email)
          }
        })
        setOwners(Array.from(byId, ([id, name]) => ({ id, name })))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [active, conciergeId])

  const extraParams = useMemo(
    () => ({
      concierge_user_id: conciergeId,
      type,
      account_owner_id: accountOwnerId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
    [accountOwnerId, conciergeId, dateFrom, dateTo, type]
  )

  const query = useAdminPaginatedQuery(adminApi.fetchActivityFeed, {
    extraParams,
    enabled: active,
    defaultSortColumn: "timestamp",
    defaultSortDirection: "desc",
  })

  const toolbar = (
    <Row gap={1.5} sx={{ flexWrap: "wrap", alignItems: "center" }}>
      <TextField
        select
        size="small"
        label="Type"
        value={type}
        onChange={(event) => setType(event.target.value)}
        sx={{ minWidth: 160 }}
      >
        {ACTIVITY_TYPE_FILTERS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Parent account"
        value={accountOwnerId}
        onChange={(event) => setAccountOwnerId(event.target.value)}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All accounts</MenuItem>
        {owners.map((owner) => (
          <MenuItem key={owner.id} value={owner.id}>
            {owner.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="date"
        size="small"
        label="From"
        value={dateFrom}
        onChange={(event) => setDateFrom(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        type="date"
        size="small"
        label="To"
        value={dateTo}
        onChange={(event) => setDateTo(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      {type === "calls" ? (
        <AdminExportCsvButton
          exportFn={adminApi.exportCallLogs}
          params={{ concierge_user_id: conciergeId }}
          filenamePrefix={`concierge-${conciergeId}-call-logs`}
        />
      ) : null}
    </Row>
  )

  return (
    <>
      {query.loadError ? (
        <Alert severity="error">{query.loadError}</Alert>
      ) : null}
      <AdminListingTable
        rows={query.rows}
        isLoading={query.isLoading}
        pageIndex={query.pageIndex}
        setPageIndex={query.setPageIndex}
        pageSize={query.pageSize}
        setPageSize={query.setPageSize}
        totalResultsSize={query.totalResultsSize}
        columnCount={7}
        toolbar={toolbar}
        tableHead={
          <AdminTableHeadRow>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Type
            </AdminHeaderTableCell>
            <AdminSortableHeaderCell
              columnName="timestamp"
              label="When"
              query={query}
              minWidth={{ xs: 72, sm: 88 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
              By
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Parent account
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 140 }}>
              Lead
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 160, sm: 220 }}>
              Details
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 64, sm: 80 }}>
              Recording
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {query.rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <KindCell row={row} />
            <AdminTruncatedCell value={formatWhen(row.timestamp)} />
            <AdminTruncatedCell
              value={
                row.actor
                  ? actorLabel(row.actor)
                  : row.direction === "incoming"
                  ? "Lead"
                  : "—"
              }
            />
            <AdminTruncatedCell value={row.account_owner?.name} />
            <AdminTruncatedCell
              value={counterpartyLabel(row.counterparty)}
              limit={28}
            />
            <SummaryCell row={row} onOpenThread={setOpenThread} />
            <RecordingCell row={row} />
          </AdminTableRow>
        ))}
      </AdminListingTable>
      <AdminActivityThreadDrawer
        thread={openThread}
        onClose={() => setOpenThread(null)}
      />
    </>
  )
}

ConciergeActivityTab.propTypes = {
  conciergeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  active: PropTypes.bool.isRequired,
}
